import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import SavedFilters from '../components/SavedFilters';
import AreaCombobox from '../components/AreaCombobox';
import TicketTimeEstimateInline from '../components/TicketTimeEstimateInline';
import { formatRomeDate } from '../lib/datetime';
import {
  Plus,
  Search,
  Filter,
  Ticket,
  Clock,
  User,
  Building2,
  Loader2,
  ChevronRight,
  AlertTriangle,
  X,
  Wrench
} from 'lucide-react';

const priorityLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const TicketsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [activeTechCount, setActiveTechCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Duplicate detection
  const [duplicateInfo, setDuplicateInfo] = useState(null); // { existing: {...} }

  // Paginazione + archivio
  const PAGE_SIZE = 50;
  const DEFAULT_DAYS_WINDOW = 60;
  const [showArchive, setShowArchive] = useState(false); // false = ultimi 60gg, true = tutto
  const [totalTickets, setTotalTickets] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterHotel, setFilterHotel] = useState(searchParams.get('hotel') || 'all');
  const [filterAssignee, setFilterAssignee] = useState(searchParams.get('assigned_to') || 'all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [hotelAreas, setHotelAreas] = useState({ rooms: [], common_areas: [], all_areas: [] });

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    hotel_id: '',
    area: '',
    category_id: '',
    priority: 'media',
    is_room_occupied: false,
    operator_signature: ''
  });

  const isFrontOffice = user?.role === 'frontoffice';

  const buildTicketsParams = (skip = 0) => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('skip', String(skip));
    // Filtri server-side (riducono drasticamente il payload)
    if (filterStatus !== 'all') params.set('status', filterStatus);
    if (filterPriority !== 'all') params.set('priority', filterPriority);
    if (filterHotel !== 'all') params.set('hotel_id', filterHotel);
    if (filterAssignee !== 'all') params.set('assigned_to', filterAssignee);
    if (filterCategory !== 'all') params.set('category_id', filterCategory);
    // Finestra temporale di default: ultimi 60 giorni + esclude archivio.
    // Toggle "Mostra archivio": rimuove il filtro data E include ticket archiviati (Sprint 2).
    if (!showArchive) {
      const from = new Date();
      from.setDate(from.getDate() - DEFAULT_DAYS_WINDOW);
      params.set('from_date', from.toISOString());
    } else {
      params.set('include_archived', 'true');
    }
    return params.toString();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ticketsRes, hotelsRes, categoriesRes] = await Promise.all([
        api.get(`/tickets?${buildTicketsParams(0)}`),
        api.get('/hotels'),
        api.get('/categories')
      ]);
      // Backward-compat: il backend ora ritorna { items, total, has_more }
      const payload = ticketsRes.data;
      if (Array.isArray(payload)) {
        setTickets(payload);
        setTotalTickets(payload.length);
        setHasMore(false);
      } else {
        setTickets(payload.items || []);
        setTotalTickets(payload.total || 0);
        setHasMore(!!payload.has_more);
      }
      setHotels(hotelsRes.data);
      setCategories(categoriesRes.data);

      // Fetch technicians if admin
      if (user?.role === 'super_admin' || user?.role === 'hotel_manager' || user?.role === 'capo_manutentore') {
        try {
          const techRes = await api.get('/users');
          setTechnicians(techRes.data.filter(u => u.role === 'technician'));
        } catch (e) {
          console.log('Could not fetch technicians list');
        }
      }

      if (['super_admin', 'capo_manutentore', 'hotel_manager'].includes(user?.role)) {
        try {
          const cnt = await api.get('/dashboard/active-technicians-count');
          setActiveTechCount(cnt.data.count);
        } catch (e) {
          setActiveTechCount(null);
        }
      }
    } catch (err) {
      toast.error('Errore nel caricamento dei ticket');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get(`/tickets?${buildTicketsParams(tickets.length)}`);
      const payload = res.data;
      const newItems = payload.items || [];
      setTickets(prev => [...prev, ...newItems]);
      setHasMore(!!payload.has_more);
    } catch (e) {
      toast.error('Errore caricamento');
    } finally {
      setLoadingMore(false);
    }
  };

  // Fetch hotel areas when hotel changes in new ticket form
  const fetchHotelAreas = async (hotelId) => {
    if (!hotelId) return;
    try {
      const { data } = await api.get(`/hotels/${hotelId}/areas`);
      setHotelAreas(data);
    } catch (e) {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterPriority, filterHotel, filterAssignee, filterCategory, showArchive]);

  const submitTicket = async (force = false) => {
    const sig = (newTicket.operator_signature || '').trim();
    if (isFrontOffice && sig.length < 2) {
      toast.error('Firma operatore obbligatoria (minimo 2 caratteri)');
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...newTicket, operator_signature: sig || null, force_create: force };
      await api.post('/tickets', payload);
      toast.success('Ticket creato con successo');
      setCreateOpen(false);
      setDuplicateInfo(null);
      setNewTicket({
        title: '',
        description: '',
        hotel_id: '',
        area: '',
        category_id: '',
        priority: 'media',
        is_room_occupied: false,
        operator_signature: ''
      });
      fetchData();
    } catch (err) {
      // 409 = duplicate detected → mostra AlertDialog
      if (err?.response?.status === 409 && err?.response?.data?.detail?.code === 'duplicate_ticket') {
        setDuplicateInfo(err.response.data.detail);
        return;
      }
      const msg = err?.response?.data?.detail || 'Errore nella creazione del ticket';
      toast.error(typeof msg === 'string' ? msg : 'Errore nella creazione del ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (submitting) return;
    await submitTicket(false);
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filterStatus !== 'all' && ticket.status !== filterStatus) return false;
    if (filterPriority !== 'all' && ticket.priority !== filterPriority) return false;
    if (filterHotel !== 'all' && ticket.hotel_id !== filterHotel) return false;
    if (filterCategory !== 'all' && ticket.category_id !== filterCategory) return false;
    if (filterAssignee !== 'all') {
      if (filterAssignee === 'unassigned') {
        if (ticket.assigned_to) return false;
      } else if (ticket.assigned_to !== filterAssignee) {
        return false;
      }
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return ticket.title.toLowerCase().includes(query) ||
             ticket.description.toLowerCase().includes(query) ||
             ticket.area.toLowerCase().includes(query);
    }
    return true;
  });

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel?.name || 'N/A';
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.name || 'Altro';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tickets-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Ticket</h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <p className="text-slate-500">
              <span className="font-bold text-slate-700">{filteredTickets.length}</span> mostrati
              {totalTickets > tickets.length && (
                <span className="text-slate-400"> · {totalTickets} totali nel periodo</span>
              )}
            </p>
            {!showArchive && (
              <button
                onClick={() => setShowArchive(true)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-semibold hover:bg-amber-100 transition"
                data-testid="show-archive-button"
                title="Per default vedi solo ticket degli ultimi 60 giorni — clicca per mostrare lo storico completo"
              >
                <Clock className="w-3 h-3" /> Mostra archivio (oltre 60 giorni)
              </button>
            )}
            {showArchive && (
              <button
                onClick={() => setShowArchive(false)}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 font-semibold hover:bg-blue-100 transition"
                data-testid="hide-archive-button"
              >
                <X className="w-3 h-3" /> Torna agli ultimi 60 giorni
              </button>
            )}
            {activeTechCount !== null && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold"
                data-testid="active-tech-badge"
                title="Tecnici con almeno un ticket in carico (assegnato, in lavorazione o in attesa pezzi)"
              >
                <Wrench className="w-3.5 h-3.5" />
                {activeTechCount} {activeTechCount === 1 ? 'tecnico' : 'tecnici'} al lavoro ora
              </span>
            )}
          </div>
        </div>
        
        {(user?.role !== 'technician') && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="create-ticket-button">
                <Plus className="w-4 h-4" />
                Nuovo Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Crea Nuovo Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    placeholder="Descrivi brevemente il problema"
                    required
                    data-testid="ticket-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Descrizione *</Label>
                  <Textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    placeholder="Dettagli del problema..."
                    required
                    data-testid="ticket-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hotel *</Label>
                    <Select
                      value={newTicket.hotel_id}
                      onValueChange={(v) => {
                        setNewTicket({...newTicket, hotel_id: v, area: ''});
                        fetchHotelAreas(v);
                      }}
                      required
                    >
                      <SelectTrigger data-testid="ticket-hotel-select">
                        <SelectValue placeholder="Seleziona hotel" />
                      </SelectTrigger>
                      <SelectContent>
                        {hotels.map(h => (
                          <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Area/Camera *</Label>
                    <AreaCombobox
                      value={newTicket.area}
                      onChange={(v) => setNewTicket({...newTicket, area: v})}
                      rooms={hotelAreas.rooms || []}
                      commonAreas={hotelAreas.common_areas || []}
                      disabled={!newTicket.hotel_id}
                      placeholder={
                        !newTicket.hotel_id
                          ? "Seleziona prima l'hotel"
                          : (hotelAreas.all_areas?.length > 0
                              ? 'Scegli o digita...'
                              : 'es. 301, Hall, Reception')
                      }
                      testId="tickets-area-combobox"
                      inputTestId="ticket-area-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={newTicket.category_id}
                      onValueChange={(v) => setNewTicket({...newTicket, category_id: v})}
                    >
                      <SelectTrigger data-testid="ticket-category-select">
                        <SelectValue placeholder="Seleziona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priorità</Label>
                    <Select
                      value={newTicket.priority}
                      onValueChange={(v) => setNewTicket({...newTicket, priority: v})}
                    >
                      <SelectTrigger data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgente">Urgente</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="bassa">Bassa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_room_occupied"
                    checked={newTicket.is_room_occupied}
                    onChange={(e) => setNewTicket({...newTicket, is_room_occupied: e.target.checked})}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="is_room_occupied">Camera occupata</Label>
                </div>

                {/* AI-powered time estimate based on historical data */}
                <TicketTimeEstimateInline
                  categoryId={newTicket.category_id}
                  hotelId={newTicket.hotel_id}
                  area={newTicket.area}
                />

                {/* Firma operatore — visibile a tutti, obbligatoria per Front Office */}
                <div className="pt-3 border-t border-slate-100">
                  <Label htmlFor="operator_signature" className="flex items-center gap-1">
                    Firma operatore
                    {isFrontOffice && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="operator_signature"
                    type="text"
                    value={newTicket.operator_signature}
                    onChange={(e) => setNewTicket({...newTicket, operator_signature: e.target.value})}
                    placeholder={isFrontOffice ? 'Inserisci il tuo nome (es. Mario Rossi)' : 'Opzionale'}
                    required={isFrontOffice}
                    minLength={isFrontOffice ? 2 : undefined}
                    maxLength={80}
                    data-testid="ticket-operator-signature-input"
                    className="mt-1.5"
                  />
                  {isFrontOffice && (
                    <p className="text-xs text-slate-500 mt-1">
                      Obbligatoria: identifica l'operatore che ha aperto il ticket (account Front Office condiviso).
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={submitting}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="ticket-submit-button" disabled={submitting}>
                    {submitting ? 'Creazione in corso...' : 'Crea Ticket'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca ticket..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="ticket-search-input"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-full md:w-40" data-testid="filter-priority">
                <SelectValue placeholder="Priorità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le priorità</SelectItem>
                {Object.entries(priorityLabels).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterHotel} onValueChange={setFilterHotel}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-hotel">
                <SelectValue placeholder="Hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli hotel</SelectItem>
                {hotels.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtri aggiuntivi: tecnico + categoria */}
          {(technicians.length > 0 || categories.length > 0) && (
            <div className="flex flex-col md:flex-row gap-4">
              {technicians.length > 0 && (
                <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                  <SelectTrigger className="w-full md:w-56" data-testid="filter-assignee">
                    <SelectValue placeholder="Tecnico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tecnici</SelectItem>
                    <SelectItem value="unassigned">Non assegnati</SelectItem>
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full md:w-56" data-testid="filter-category">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le categorie</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Saved filters and clear */}
          <div className="flex items-center justify-between">
            <SavedFilters
              currentFilters={{
                status: filterStatus,
                priority: filterPriority,
                hotel: filterHotel,
                assignee: filterAssignee,
                category: filterCategory,
                search: searchQuery
              }}
              onLoadFilter={(filters) => {
                setFilterStatus(filters.status || 'all');
                setFilterPriority(filters.priority || 'all');
                setFilterHotel(filters.hotel || 'all');
                setFilterAssignee(filters.assignee || 'all');
                setFilterCategory(filters.category || 'all');
                setSearchQuery(filters.search || '');
              }}
            />
            
            {(filterStatus !== 'all' || filterPriority !== 'all' || filterHotel !== 'all' || filterAssignee !== 'all' || filterCategory !== 'all' || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-500"
                onClick={() => {
                  setFilterStatus('all');
                  setFilterPriority('all');
                  setFilterHotel('all');
                  setFilterAssignee('all');
                  setFilterCategory('all');
                  setSearchQuery('');
                  // Pulisci anche i param URL
                  const params = new URLSearchParams(searchParams);
                  let dirty = false;
                  if (params.get('assigned_to')) { params.delete('assigned_to'); dirty = true; }
                  if (params.get('hotel')) { params.delete('hotel'); dirty = true; }
                  if (dirty) setSearchParams(params, { replace: true });
                }}
                data-testid="clear-filters-button"
              >
                <X className="w-4 h-4 mr-1" />
                Cancella filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets list */}
      <ScrollArea className="h-[calc(100vh-320px)]">
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="border-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              data-testid={`ticket-card-${ticket.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`priority-${ticket.priority} text-xs`}>
                        {priorityLabels[ticket.priority]}
                      </Badge>
                      <Badge variant="secondary" className={`status-${ticket.status} text-xs`}>
                        {statusLabels[ticket.status]}
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 truncate">{ticket.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{ticket.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1 min-w-0">
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{getHotelName(ticket.hotel_id)}</span>
                      </span>
                      <span className="flex items-center gap-1 min-w-0">
                        <Ticket className="w-3 h-3 shrink-0" />
                        <span className="truncate">{ticket.area}</span>
                      </span>
                      <span className="flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" />
                        {formatRomeDate(ticket.created_at)}
                      </span>
                      {ticket.assigned_to ? (
                        <span className="flex items-center gap-1 font-medium text-slate-700 min-w-0 basis-full sm:basis-auto" data-testid={`ticket-assignee-${ticket.id}`}>
                          <User className="w-3 h-3 shrink-0" />
                          <span className="truncate">In carico a: {ticket.assigned_to_name || 'Tecnico'}</span>
                          {ticket.assigned_to_is_ex && (
                            <span
                              className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 shrink-0"
                              title="Ex dipendente — non più in servizio"
                              data-testid={`assignee-ex-badge-${ticket.id}`}
                            >
                              Ex
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600 shrink-0" data-testid={`ticket-unassigned-${ticket.id}`}>
                          <User className="w-3 h-3" />
                          Da assegnare
                        </span>
                      )}
                      {ticket.operator_signature && (
                        <span
                          className="flex items-center gap-1 text-slate-600"
                          data-testid={`ticket-operator-signature-${ticket.id}`}
                          title="Operatore Front Office che ha aperto il ticket"
                        >
                          <User className="w-3 h-3" />
                          Firma: <span className="font-medium">{ticket.operator_signature}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filteredTickets.length === 0 && (
            <div className="text-center py-12">
              <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun ticket trovato</p>
            </div>
          )}

          {/* Load more — solo se non ci sono filtri client side che alterano il count */}
          {hasMore && filteredTickets.length === tickets.length && (
            <div className="text-center py-4">
              <Button
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                className="min-w-[200px]"
                data-testid="load-more-button"
              >
                {loadingMore
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <ChevronRight className="w-4 h-4 mr-2" />}
                Carica altri ({totalTickets - tickets.length} rimasti)
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Smart Duplicate Detection Dialog */}
      <AlertDialog open={!!duplicateInfo} onOpenChange={(o) => { if (!o) setDuplicateInfo(null); }}>
        <AlertDialogContent data-testid="duplicate-ticket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Possibile ticket duplicato
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  È già stato aperto un ticket simile per questa stessa area e categoria negli ultimi <strong>30 minuti</strong>.
                  Verifica se ne stai duplicando uno esistente:
                </p>
                {duplicateInfo?.existing_ticket && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge className={`text-[10px] ${
                        duplicateInfo.existing_ticket.priority === 'urgente' ? 'bg-red-600 text-white' :
                        duplicateInfo.existing_ticket.priority === 'alta' ? 'bg-orange-500 text-white' :
                        'bg-blue-500 text-white'
                      }`}>{duplicateInfo.existing_ticket.priority}</Badge>
                      <Badge variant="secondary" className="text-[10px]">{duplicateInfo.existing_ticket.status}</Badge>
                    </div>
                    <p className="font-semibold text-slate-900">{duplicateInfo.existing_ticket.title}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Creato da {duplicateInfo.existing_ticket.created_by_name || 'utente'}
                      {duplicateInfo.existing_ticket.operator_signature && ` · Firma: ${duplicateInfo.existing_ticket.operator_signature}`}
                      {duplicateInfo.existing_ticket.created_at && ` · ${formatRomeDate(duplicateInfo.existing_ticket.created_at)}`}
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Puoi aprire quello esistente per aggiungere note, oppure creare comunque un nuovo ticket se è un problema diverso.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel data-testid="duplicate-cancel-btn">Annulla</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                const id = duplicateInfo?.existing_ticket?.id;
                setDuplicateInfo(null);
                setCreateOpen(false);
                if (id) navigate(`/tickets/${id}`);
              }}
              data-testid="duplicate-open-existing-btn"
            >
              Apri quello esistente
            </Button>
            <AlertDialogAction
              onClick={() => submitTicket(true)}
              className="bg-blue-900 hover:bg-blue-800"
              data-testid="duplicate-force-create-btn"
            >
              Crea comunque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TicketsPage;
